import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../supabase/supabase';

const VenueContext = createContext();

export const useVenues = () => {
  const context = useContext(VenueContext);
  if (!context) throw new Error('useVenues must be used within a VenueProvider');
  return context;
};

export const VenueProvider = ({ children }) => {
  const { user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchVenues();
    } else {
      setVenues([]);
    }
  }, [user]);

  const fetchVenues = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('venues_1780591882954')
      .select('*, pricing_overrides_1780591882954(*)');
    
    if (error) {
      console.error('Error fetching venues:', error);
    } else {
      // Map data to match our UI structure
      const formatted = data.map(v => ({
        ...v,
        location: { address: v.address, coordinates: { lat: v.lat, lng: v.lng } },
        priceOverrides: v.pricing_overrides_1780591882954?.reduce((acc, curr) => {
          acc[curr.date] = curr.price;
          return acc;
        }, {}) || {}
      }));
      setVenues(formatted);
    }
    setLoading(false);
  };

  const addVenue = async (venueData) => {
    const { data, error } = await supabase
      .from('venues_1780591882954')
      .insert([{
        owner_id: user.id,
        name: venueData.name,
        type: venueData.type,
        address: venueData.location.address,
        lat: venueData.location.coordinates.lat,
        lng: venueData.location.coordinates.lng,
        photos: venueData.photos,
        facilities: venueData.facilities,
        max_capacity: venueData.maxCapacity,
        recommended_capacity: venueData.recommendedCapacity,
        price_per_night: venueData.pricePerNight,
        pricing_model: venueData.pricingModel,
        occupancy_rates: venueData.occupancyRates,
        price_includes_utilities: venueData.priceIncludesUtilities,
        utilities: venueData.utilities,
        selected_utilities: venueData.selectedUtilities
      }])
      .select()
      .single();

    if (error) throw error;
    await fetchVenues();
    return data;
  };

  const updateVenue = async (id, updates) => {
    // Flatten nested objects for Supabase
    const dbUpdates = { ...updates };
    if (updates.location) {
      dbUpdates.address = updates.location.address;
      dbUpdates.lat = updates.location.coordinates.lat;
      dbUpdates.lng = updates.location.coordinates.lng;
      delete dbUpdates.location;
    }
    
    // Map camelCase to snake_case if needed
    if (updates.maxCapacity) dbUpdates.max_capacity = updates.maxCapacity;
    if (updates.recommendedCapacity) dbUpdates.recommended_capacity = updates.recommendedCapacity;

    const { error } = await supabase
      .from('venues_1780591882954')
      .update(dbUpdates)
      .eq('id', id);

    if (error) throw error;
    await fetchVenues();
  };

  const updateVenuePricing = async (venueId, dateStr, price) => {
    if (price === null || price === '') {
      await supabase
        .from('pricing_overrides_1780591882954')
        .delete()
        .eq('venue_id', venueId)
        .eq('date', dateStr);
    } else {
      await supabase
        .from('pricing_overrides_1780591882954')
        .upsert({
          venue_id: venueId,
          date: dateStr,
          price: parseFloat(price)
        });
    }
    await fetchVenues();
  };

  const deleteVenue = async (id) => {
    const { error } = await supabase.from('venues_1780591882954').delete().eq('id', id);
    if (error) throw error;
    setVenues(prev => prev.filter(v => v.id !== id));
  };

  const getVenue = (id) => venues.find(v => v.id === id);

  const value = { venues, loading, addVenue, updateVenue, updateVenuePricing, deleteVenue, getVenue };

  return (
    <VenueContext.Provider value={value}>
      {children}
    </VenueContext.Provider>
  );
};